import { Button } from "@/components/ui/button";
import { cn } from "@/utils";
import type { Email } from "@cohesive/api-entities";
import { formatDistanceToNow } from "date-fns";
import { ChevronDown, ChevronRight, Mail } from "lucide-react";
import { useState } from "react";

interface ThreadMessagesProps {
  messages: Email[];
  onMessageClick: (message: Email, index: number) => void;
  currentMessageIndex: number;
}

export function ThreadMessages({
  messages,
  onMessageClick,
  currentMessageIndex,
}: ThreadMessagesProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (messages.length <= 1) {
    return null;
  }

  return (
    <div className="border-t bg-muted/20 px-6 py-4">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 mb-3 text-sm hover:bg-accent"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Hide thread ({messages.length}{" "}
              {messages.length === 1 ? "message" : "messages"})
            </>
          ) : (
            <>
              <ChevronRight className="h-4 w-4 mr-1" />
              Show thread ({messages.length}{" "}
              {messages.length === 1 ? "message" : "messages"})
            </>
          )}
        </Button>

        {isExpanded && (
          <div className="space-y-2 border-l-2 border-muted pl-4">
            {messages.map((message, index) => {
              const isActive = currentMessageIndex === index;

              return (
                <div
                  key={message.id}
                  className={cn(
                    "flex items-center gap-3 text-sm py-2 px-3 rounded cursor-pointer transition-colors",
                    isActive
                      ? "bg-primary/10 border border-primary/20"
                      : "hover:bg-accent/50",
                  )}
                  onClick={() => onMessageClick(message, index)}
                >
                  <Mail
                    className={cn(
                      "h-4 w-4 shrink-0",
                      isActive ? "text-primary" : "text-muted-foreground",
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "font-medium shrink-0",
                          isActive ? "text-primary" : "text-foreground",
                        )}
                      >
                        {message.sender_name || message.sender_address}
                      </span>
                      <span
                        className={cn(
                          "text-xs shrink-0",
                          isActive
                            ? "text-primary/70"
                            : "text-muted-foreground",
                        )}
                      >
                        {formatDistanceToNow(new Date(message.timestamp), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
